import React, { useEffect, useState } from 'react';
import { Typography, Upload, Card, Select, Button, Table, Tag, Space, Alert, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../api';

// Admin > File Uploads > Campaign Updates Upload (admin only): the
// historical updates from the old Google Sheet, one tab per campaign. Each
// tab is given its team(s) here; every row becomes an update with
// Acknowledgement Required No (never on a To Do, no alert). Check first;
// Import adds the rows without a problem. A row already imported (same date,
// topic and team) is skipped, so the same file can be loaded again safely.

// Column headers, matched loosely (case, spaces and underscores ignored).
const COLS = {
  date: ['dateofupdate', 'date'],
  topic: ['topic', 'title'],
  description: ['description'],
  link: ['link', 'url'],
  status: ['status'],
  source: ['sourceid', 'source'],
  training: ['updatedintraining', 'training'],
};
const norm = (h) => String(h).toLowerCase().replace(/[\s_]/g, '');

export default function CampaignUpdatesUploadPage() {
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);       // [{ sheet, team_ids, rows }]
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState(null);     // the last check / import
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/api/teams').then((r) => setTeams(r.data || [])).catch(() => setTeams([]));
  }, []);

  const readFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const out = [];
        wb.SheetNames.forEach((name) => {
          const json = XLSX.utils.sheet_to_json(wb.Sheets[name], { defval: '', raw: false });
          if (!json.length) return;
          const headers = Object.keys(json[0]);
          const pick = {};
          Object.entries(COLS).forEach(([k, names]) => { pick[k] = headers.find((h) => names.includes(norm(h))); });
          const rows = json.map((r) => Object.fromEntries(Object.keys(COLS).map((k) => [k, pick[k] ? String(r[pick[k]] ?? '').trim() : ''])))
            .filter((r) => r.date || r.topic || r.link);
          if (rows.length) out.push({ sheet: name, team_ids: [], rows, missing: ['date', 'topic', 'link', 'status'].filter((k) => !pick[k]) });
        });
        if (!out.length) { message.error('No rows found in the file.'); return; }
        setGroups(out);
        setFileName(file.name);
        setResult(null);
      } catch {
        message.error('Could not read the file.');
      }
    };
    reader.readAsArrayBuffer(file);
    return false;   // read here, never sent as a file
  };

  const send = async (save) => {
    setBusy(true);
    try {
      const res = await api.post('/api/campaign-updates/import', {
        save, groups: groups.map((g) => ({ sheet: g.sheet, team_ids: g.team_ids, rows: g.rows })),
      });
      setResult({ ...res.data, saved: save });
      if (save) message.success(`${res.data.added} update${res.data.added === 1 ? '' : 's'} imported.`);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  const setTeamsFor = (i, ids) => {
    setGroups((cur) => cur.map((g, j) => (j === i ? { ...g, team_ids: ids } : g)));
    setResult(null);   // teams changed: check again
  };

  const allRows = (result?.groups || []).flatMap((g) => g.rows.map((r, i) => ({ ...r, sheet: g.sheet, key: `${g.sheet}|${i}` })));
  const ok = allRows.filter((r) => !r.Problem).length;
  const teamOptions = teams.map((t) => ({ value: t.ID, label: t.Name }));

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>Campaign Updates Upload</Typography.Title>
      <Typography.Paragraph type="secondary">
        One tab per campaign. Columns: Date of Update, Topic, Link, Status (Active or Inactive); optional Description,
        SourceID, Updated in Training (Yes or No). Every row is imported with Acknowledgement Required No.
      </Typography.Paragraph>

      <Upload.Dragger accept=".xlsx,.xls" showUploadList={false} beforeUpload={readFile} disabled={busy}
        style={{ marginBottom: 16 }}>
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p>{fileName ? `${fileName} — click or drop to load another` : 'Click or drop the workbook here'}</p>
      </Upload.Dragger>

      {groups.map((g, i) => (
        <Card key={g.sheet} size="small" style={{ marginBottom: 8 }}
          title={<Space>{g.sheet}<Tag>{g.rows.length} row{g.rows.length === 1 ? '' : 's'}</Tag></Space>}>
          {g.missing.length > 0 && (
            <Alert type="warning" showIcon style={{ marginBottom: 8 }}
              message={`No column found for: ${g.missing.join(', ')}`} />
          )}
          <Space>
            <span>Team(s):</span>
            <Select mode="multiple" showSearch optionFilterProp="label" placeholder="Pick the team(s) for this tab"
              style={{ minWidth: 360 }} value={g.team_ids} onChange={(v) => setTeamsFor(i, v)} options={teamOptions} />
          </Space>
        </Card>
      ))}

      {groups.length > 0 && (
        <Space style={{ margin: '8px 0 16px' }}>
          <Button onClick={() => send(false)} loading={busy && !result?.saved}>Check</Button>
          <Button type="primary" disabled={!result || result.saved || !ok} loading={busy}
            onClick={() => send(true)}>Import {ok || ''} row{ok === 1 ? '' : 's'}</Button>
          {result && !result.saved && (
            <Typography.Text type="secondary">{ok} ready, {allRows.length - ok} with a problem (not imported)</Typography.Text>
          )}
        </Space>
      )}

      {result && (
        <Table rowKey="key" size="small" dataSource={allRows} scroll={{ x: 'max-content' }} sticky={{ offsetHeader: 0 }}
          pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ['50', '100', '200', '500'],
            showTotal: (t) => `${t} rows` }}
          columns={[
            { title: 'Tab', dataIndex: 'sheet', key: 'sheet', width: 140 },
            { title: 'Date of Update', key: 'date', width: 120, render: (_, r) => r.Date || r.date || '—' },
            { title: 'Topic', dataIndex: 'topic', key: 'topic', width: 280,
              render: (v) => <span style={{ whiteSpace: 'normal' }}>{v || '—'}</span> },
            { title: 'Link', dataIndex: 'link', key: 'link', width: 80,
              render: (v) => (v ? <a href={v} target="_blank" rel="noopener noreferrer">Open</a> : '—') },
            { title: 'Status', key: 'status', width: 90, render: (_, r) => r.Status || '—' },
            { title: 'Source', key: 'source', width: 120, render: (_, r) => r.SourceName || r.source || '—' },
            { title: 'Updated in Training', dataIndex: 'training', key: 'training', width: 150, render: (v) => v || 'No' },
            { title: '', key: 'problem', width: 260,
              render: (_, r) => (r.Problem
                ? <Tag color={r.Problem === 'Already imported' ? 'default' : 'red'}>{r.Problem}</Tag>
                : <Tag color="green">{result.saved ? 'Imported' : 'Ready'}</Tag>) },
          ]} />
      )}
    </div>
  );
}
